import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import moment from 'moment';
import { Apollo } from 'apollo-angular';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import {
  subscribe_to_sequence,
  activate_sequence,
  update_pipeline_project,
  delete_pipeline_project_date,
  update_pipeline_project_and_forecast_approvals,
  update_forecast_approvals,
} from './graphql/statusesAndSequences';
import { UnsubscribeConfirmationComponent } from './unsubscribe-confirmation/unsubscribe-confirmation.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { DeadDealConfirmationComponent } from './dead-deal-confirmation/dead-deal-confirmation.component';
import { ResurrectConfirmationComponent } from './resurrect-confirmation/resurrect-confirmation.component';
import { ConfirmationModalComponent } from '../../../components/confirmation-modal/confirmation-modal.component';
import momenttz from 'moment-timezone';
import { delete_forecast_approval_date } from '../../deals-home/graphql/getProjectDetails';
import { DealDatesService } from '../deal-dates/deal-dates.service';

const SUBSCRIBE_TO_SEQUENCE = subscribe_to_sequence;
const ACTIVATE_SEQUENCE = activate_sequence;
const UPDATE_PIPELINE_PROJECT = update_pipeline_project;
const DELETE_PIPELINE_PROJECT_DATE = delete_pipeline_project_date;

@Component({
    selector: 'deal-status-and-pipeline-dates',
    templateUrl: './status-and-pipeline-dates.component.html',
    styleUrls: ['./status-and-pipeline-dates.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StatusAndPipelineDatesComponent implements OnChanges {
  @Input() statuses: any;
  @Input() sequences: any;
  @Input() deadSequence: any;
  @Input() deadStatus: any;
  @Input() dates: any;
  @Input() forecastDates: any;
  @Input() forecastApprovalID: string;
  @Input() permissions: any;
  @Input() projectID: string;
  @Input() projectName: string;
  @Input() developer: any;
  @Input() pipelineProjects: any;
  @Input() hasRequirements: boolean;
  @Input() projectSubstatusDates: any[];
  @Input() projectSubstatuses: any[];
  @Input() subsequences: any[];
  @Input() projectCredits: any[]

  @Output() projectSubstatusDatesChange: EventEmitter<any[]> =
    new EventEmitter();
  @Output() projectSubstatusesChange: EventEmitter<any[]> = new EventEmitter();
  @Output() requirementsChange: EventEmitter<any> = new EventEmitter();
  @Output() applicationsChange: EventEmitter<any> = new EventEmitter();
  @Output() datesChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() statusesChange = new EventEmitter<any>();
  availableSequences: any = [];
  isDead: boolean = false;
  deadDate: null | string = null;
  hasDeadPermissions: boolean = false;
  statusSequenceDates: any;
  closingStatusName: string = '';
  deadProjectStatusID: string = '';
  isTimelineView = true;
  dateDelete: any;

  constructor(
    private apollo: Apollo,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog,
    private dealDatesService: DealDatesService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.hasOwnProperty('dates') ||
      changes.hasOwnProperty('pipelineProjects') ||
      changes.hasOwnProperty('statuses') ||
      changes.hasOwnProperty('forecastDates')
    ) {
      this.setStatusesAndPipelineDates();
    }
  }

  ngOnInit() {
    this.isMobile = this.getIsMobile();
    window.onresize = () => {
      this.isMobile = this.getIsMobile();
    };
  }

  isMobile = false;
  getIsMobile(): boolean {
    const w = document.documentElement.clientWidth;
    const breakpoint = 1475;
    return w < breakpoint;
  }

  setStatusesAndPipelineDates() {
    this.statusSequenceDates = this.formatDates();
    let projectCredits = this.formatCredits();
    this.projectCredits = projectCredits;
    this.hasDeadPermissions =
      this.permissions.deadStatusSequencePermissions._edit;
    this.formatAvailableSequences();
  }

  formatDates(): any {
    let pipelineDates: any = [];
    for (let i = 0; i < this.statuses.length; i++) {
      if (this.statuses[i].statusSequence.name === 'Dead') {
        this.deadProjectStatusID = this.statuses[i].id;
        this.isDead = this.statuses[i].active;
      }

      for (let j = 0; j < this.dates.length; j++) {
        //If status sequence and pipeline match
        if (
          this.statuses[i].statusSequence.name === this.dates[j].pipeline.name
        ) {
          let perm = false;
          if (
            (this.statuses[i].statusSequence.name === 'Closing' &&
              this.permissions.closingStatusSequencePermissions._edit) ||
            (this.statuses[i].statusSequence.name === 'Syndications' &&
              this.permissions.syndicationsStatusSequencePermissions._edit) ||
            (this.statuses[i].statusSequence.name === 'Managing' &&
              this.permissions.managingStatusSequencePermissions._edit) ||
            (this.statuses[i].statusSequence.name === 'Dead' &&
              this.permissions.deadStatusSequencePermissions._edit)
          ) {
            perm = true;
          }

          let pipelineProjectID = '';
          for (let k = 0; k < this.pipelineProjects.length; k++) {
            if (
              this.dates[j].pipeline.name ===
              this.pipelineProjects[k].pipeline.name
            ) {
              pipelineProjectID = this.pipelineProjects[k].id;
              break;
            }
          }

          let initializeStatusDates: any = [];
          let sequenceDates = {
            sequence: {
              id: this.statuses[i].statusSequence.id,
              name: this.statuses[i].statusSequence.name,
              isEditMode: false,
              hasPermissions: perm,
              projectStatusID: this.statuses[i].id,
              pipelineProjectid: pipelineProjectID,
              active: this.statuses[i].active,
              is_primary: this.statuses[i].is_primary,
              statuses: this.statuses[i].statusSequence.status_sequence_details,
            },
            activeStatusIndex: null,
            activeStatusName: null,
            isDead: this.isDead,
            statusDates: initializeStatusDates,
          };

          //Loop through each status sequence to match with the correct pipeline
          for (
            let k = 0;
            k < this.statuses[i].statusSequence.status_sequence_details.length;
            k++
          ) {
            if (
              this.statuses[i].status &&
              this.statuses[i].status.id ===
                this.statuses[i].statusSequence.status_sequence_details[k]
                  .status.id
            ) {
              sequenceDates.activeStatusIndex =
                this.statuses[i].statusSequence.status_sequence_details[
                  k
                ].index;
              sequenceDates.activeStatusName =
                this.statuses[i].statusSequence.status_sequence_details[
                  k
                ].status.name;

              if (this.statuses[i].statusSequence.name === 'Closing') {
                this.closingStatusName =
                  this.statuses[i].statusSequence.status_sequence_details[
                    k
                  ].status.name;
              }
              if (
                this.statuses[i].statusSequence.name === 'Dead' &&
                this.statuses[i].active &&
                this.dates[j].pipelineProjectDates.length > 0
              ) {
                this.deadDate = moment
                  .utc(this.dates[j].pipelineProjectDates[0]['date'])
                  .format('MM/DD/YYYY');
              }
            }

            let statusDate = {
              dateID: null,
              date: '',
              newDate: '',
              isTarget: null,
              newIsTarget: true,
              initialDateID: null,
              initialDate: '',
              newInitialDate: '',
              minDate: new Date(
                moment.utc('1970-01-01').format('MM/DD/YYYY')
              ).toISOString(),
            };

            //Loop through each status in the status sequence and match it with pipeline milestones
            //to get the date for the status
            for (
              let l = 0;
              l < this.dates[j].pipelineProjectDates.length;
              l++
            ) {
              //If there's a match, get the dates
              if (
                this.statuses[i].statusSequence.status_sequence_details[k][
                  'milestoneStatus'
                ]['id'] ===
                this.dates[j].pipelineProjectDates[l]['pipelineMilestone'][
                  'milestoneStatus'
                ]['id']
              ) {
                if (this.dates[j].pipeline.name === 'Closing') {
                  if (
                    this.statuses[i].statusSequence.status_sequence_details[
                      k
                    ].status.name.includes('Committee')
                  ) {
                    let newdate = momenttz().tz('America/Los_Angeles');
                    if (parseInt(newdate.format('H')) >= 12) {
                      statusDate.minDate = new Date(
                        newdate.add(3, 'd').format('MM/DD/YYYY')
                      ).toISOString();
                    } else {
                      statusDate.minDate = new Date(
                        newdate.add(2, 'd').format('MM/DD/YYYY')
                      ).toISOString();
                    }
                  }
                }

                if (this.dates[j].pipelineProjectDates[l]['initial_target']) {
                  statusDate.initialDateID =
                    this.dates[j].pipelineProjectDates[l]['id'];
                  statusDate.initialDate = moment
                    .utc(this.dates[j].pipelineProjectDates[l]['date'])
                    .format('MM/DD/YYYY');
                  statusDate.newInitialDate = new Date(
                    statusDate.initialDate
                  ).toISOString();
                } else {
                  statusDate.dateID =
                    this.dates[j].pipelineProjectDates[l]['id'];
                  statusDate.date = moment
                    .utc(this.dates[j].pipelineProjectDates[l]['date'])
                    .format('MM/DD/YYYY');
                  statusDate.isTarget =
                    this.dates[j].pipelineProjectDates[l]['target'];
                  statusDate.newIsTarget =
                    this.dates[j].pipelineProjectDates[l]['target'];
                  statusDate.newDate = new Date(statusDate.date).toISOString();
                }
              }
            }

            sequenceDates.statusDates.push({
              status:
                this.statuses[i].statusSequence.status_sequence_details[k][
                  'status'
                ]['id'],
              statusName:
                this.statuses[i].statusSequence.status_sequence_details[k][
                  'status'
                ]['name'],
              index:
                this.statuses[i].statusSequence.status_sequence_details[k]
                  .index,
              pmid: this.statuses[i].statusSequence.status_sequence_details[k]
                .milestoneStatus.pipeline_milestone_id,
              dates: statusDate,
              forecastDates: null,
            });
          }
          pipelineDates.push(sequenceDates);
        }
      }
    }
    pipelineDates = this.statusSequenceSort(pipelineDates);

    if (pipelineDates.length > 0) {
      for (let i = 0; i < pipelineDates.length; i++) {
        if (pipelineDates[i].sequence.name === 'Closing') {
          for (let j = 0; j < pipelineDates[i].statusDates.length; j++) {
            if (this.forecastDates && this.forecastDates.length > 0) {
              for (let k = 0; k < this.forecastDates.length; k++) {
                if (
                  pipelineDates[i].statusDates[j].pmid ===
                    this.forecastDates[k].pipelineMilestone.id &&
                  this.forecastDates[k].dateType.name === 'Forecast Approval'
                ) {
                  pipelineDates[i].statusDates[j].forecastDates = {
                    id: this.forecastDates[k].id,
                    dateTypeID: this.forecastDates[k].dateType.id,
                    date: moment
                      .utc(this.forecastDates[k].date)
                      .format('MM/DD/YYYY'),
                    newDate: new Date(
                      moment
                        .utc(this.forecastDates[k].date)
                        .format('MM/DD/YYYY')
                    ).toISOString(),
                  };
                } else if (
                  pipelineDates[i].statusDates[j].pmid !==
                    this.forecastDates[k].pipelineMilestone.id &&
                  (!pipelineDates[i].statusDates[j].forecastDates ||
                    !pipelineDates[i].statusDates[j].forecastDates.id ||
                    pipelineDates[i].statusDates[j].forecastDates.id === null)
                ) {
                  pipelineDates[i].statusDates[j].forecastDates = {
                    id: null,
                    dateTypeID: null,
                    date: '',
                    newDate: '',
                  };
                }
              }
            } else {
              pipelineDates[i].statusDates[j].forecastDates = {
                id: null,
                dateTypeID: null,
                date: '',
                newDate: '',
              };
            }
          }
        }
      }
    }
    return pipelineDates;
  }

  formatCredits(){
    let creditsArr:any[] = []
    if(this.projectCredits.length>0){
      for(let c = 0; c < this.projectCredits.length; c++){
        creditsArr.push(...this.projectCredits[c].projectCreditOutlets)
      }
    }
    return creditsArr
  }

  //This is to sort the sequences by is_primary asc, active desc
  statusSequenceSort(sequence: any): any {
    function compare(a: any, b: any) {
      if (a.sequence.is_primary === b.sequence.is_primary) {
        if (a.sequence.active === b.sequence.active) {
          if (a.sequence.name < b.sequence.name) {
            return -1;
          } else {
            return 1;
          }
        } else {
          if (a.sequence.active > b.sequence.active) {
            return -1;
          } else if (a.sequence.active < b.sequence.active) {
            return 1;
          } else {
            return 0;
          }
        }
      } else if (a.sequence.is_primary > b.sequence.is_primary) {
        return -1;
      }
      return 1;
    }
    return sequence.sort(compare);
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }

  formatAvailableSequences() {
    for (let i = 0; i < this.sequences.length; i++) {
      let hasSequence = false;
      let sequenceid = '';

      let hasPermissions =
        (this.sequences[i].name === 'Closing' &&
          this.permissions.closingStatusSequencePermissions._edit) ||
        (this.sequences[i].name === 'Syndications' &&
          this.permissions.syndicationsStatusSequencePermissions._edit) ||
        (this.sequences[i].name === 'Managing' &&
          this.permissions.managingStatusSequencePermissions._edit);

      for (let j = 0; j < this.statuses.length; j++) {
        if (this.sequences[i].id === this.statuses[j].statusSequence.id) {
          if (!this.statuses[j].active) {
            sequenceid = this.statuses[j]['id'];
          }
          hasSequence = true;
          break;
        }
      }

      if (hasPermissions && (!hasSequence || sequenceid !== '')) {
        this.availableSequences.push({
          ...this.sequences[i],
          projectSequenceID: sequenceid,
        });
      }
    }
  }

  removeFromAvailableSequences(sequenceID: string) {
    let key: number = 0;
    let found: boolean = false;
    for (let i = 0; i < this.availableSequences.length; i++) {
      if (this.availableSequences[i].id === sequenceID) {
        key = i;
        found = true;
        break;
      }
    }

    if (found) {
      this.availableSequences.splice(key, 1);
    }
  }

  subscribeToSequence(sequence: any) {
    if (sequence.projectSequenceID !== '') {
      this.apollo
        .mutate({
          mutation: ACTIVATE_SEQUENCE,
          variables: {
            id: sequence.projectSequenceID,
            active: true,
          },
        })
        .subscribe(({ data }) => {
          // @ts-ignore
          let results = data.updateStatusSequence;

          this.removeFromAvailableSequences(results.statusSequence.id);
          this.statuses = results.project.projectStatuses;
          this.dates = results.project.pipelineProjects;
          this.statusSequenceDates = this.formatDates();
          this.openSnackBar('Reactivated Status Sequence.');
        });
    } else {
      let input = {
        project_id: this.projectID,
        status_sequence_id: sequence.id,
        is_primary: sequence.name === 'Closing' || sequence.name === 'Managing',
        active: true,
      };
      this.apollo
        .mutate({
          mutation: SUBSCRIBE_TO_SEQUENCE,
          variables: {
            input: input,
          },
        })
        .subscribe(({ data }) => {
          // @ts-ignore
          let results = data.subscribeToStatusSequence;

          this.removeFromAvailableSequences(results.statusSequence.id);
          this.statuses = results.project.projectStatuses;
          this.dates = results.project.pipelineProjects;
          this.statusSequenceDates = this.formatDates();
          this.openSnackBar('Subscribed to Status Sequence.');
        });
    }
  }

  unsubscribeConfirmation(sequence: any) {
    let dealInfo = {
      deal: this.projectName,
      sequence: sequence.sequence.name,
      status: sequence.activeStatusName ? sequence.activeStatusName : '',
    };
    const ref: MatDialogRef<UnsubscribeConfirmationComponent> =
      this.dialog.open(UnsubscribeConfirmationComponent, {
        width: '600px',
        height: '300px',
        data: {
          dealInfo: dealInfo,
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true,
      });
    ref.afterClosed().subscribe((data) => {
      if (data.clicked === 'Ok') {
        this.apollo
          .mutate({
            mutation: ACTIVATE_SEQUENCE,
            variables: {
              id: sequence.sequence.projectStatusID,
              active: false,
            },
          })
          .subscribe(({ data }) => {
            // @ts-ignore
            let results = data.updateStatusSequence;

            this.statuses = results.project.projectStatuses;
            this.dates = results.project.pipelineProjects;
            this.statusSequenceDates = this.formatDates();
            this.openSnackBar('Unsubscribed from Status Sequence.');
          });
      }
    });
  }

  deadConfirmation() {
    let dealInfo = {
      deal: this.projectName,
      developer:
        this.developer && this.developer.length > 0
          ? this.developer[0].organization.name
          : '',
      closingStatus: this.closingStatusName,
    };
    const ref: MatDialogRef<DeadDealConfirmationComponent> = this.dialog.open(
      DeadDealConfirmationComponent,
      {
        width: '600px',
        height: '300px',
        data: {
          dealInfo: dealInfo,
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true,
      }
    );
    ref.afterClosed().subscribe((data) => {
      if (data.clicked === 'Ok') {
        if (this.deadProjectStatusID !== '') {
          this.apollo
            .mutate({
              mutation: ACTIVATE_SEQUENCE,
              variables: {
                id: this.deadProjectStatusID,
                is_primary: true,
                active: true,
              },
            })
            .subscribe(({ data }) => {
              // @ts-ignore
              let results = data.updateStatusSequence;

              this.applicationsChange.emit(results.project.applications);
              this.statuses = results.project.projectStatuses;
              this.dates = results.project.pipelineProjects;
              this.statusSequenceDates = this.formatDates();
              this.openSnackBar('Marked the Deal as Dead.');
            });
        } else {
          let input = {
            project_id: this.projectID,
            status_sequence_id: this.deadSequence.id,
            status_id: this.deadStatus.id,
            is_primary: true,
            active: true,
          };
          this.apollo
            .mutate({
              mutation: SUBSCRIBE_TO_SEQUENCE,
              variables: {
                input: input,
              },
            })
            .subscribe(({ data }) => {
              // @ts-ignore
              let results = data.subscribeToStatusSequence;

              this.removeFromAvailableSequences(results.statusSequence.id);
              this.statuses = results.project.projectStatuses;
              this.dates = results.project.pipelineProjects;
              this.applicationsChange.emit(results.project.applications);
              this.statusSequenceDates = this.formatDates();
              this.openSnackBar('Marked the Deal as Dead.');
            });
        }
      }
    });
  }

  resurrectConfirmation() {
    let dealInfo = {
      deal: this.projectName,
      developer:
        this.developer && this.developer.length > 0
          ? this.developer[0].organization.name
          : '',
      closingStatus: this.closingStatusName,
    };
    const ref: MatDialogRef<ResurrectConfirmationComponent> = this.dialog.open(
      ResurrectConfirmationComponent,
      {
        width: '600px',
        height: '300px',
        data: {
          dealInfo: dealInfo,
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true,
      }
    );
    ref.afterClosed().subscribe((data) => {
      if (data.clicked === 'Ok') {
        this.apollo
          .mutate({
            mutation: ACTIVATE_SEQUENCE,
            variables: {
              id: this.deadProjectStatusID,
              active: false,
            },
          })
          .subscribe(({ data }) => {
            // @ts-ignore
            let results = data.updateStatusSequence;

            this.statuses = results.project.projectStatuses;
            this.dates = results.project.pipelineProjects;
            this.statusSequenceDates = this.formatDates();
            this.isDead = false;
            for (let i = 0; i < this.statusSequenceDates.length; i++) {
              this.statusSequenceDates[i].isDead = false;
            }
            this.openSnackBar('Resurrected the Deal.');
          });
      }
    });
  }

  deleteDateConfirmation(date: any, index: number) {
    let dateType: string;
    let dateID: string;
    let pipelineDate: string;
    if (date.isInitial) {
      dateType = 'Initial';
      dateID = date.dates.initialDateID;
      pipelineDate = date.dates.initialDate;
    } else {
      dateType = date.dates.isTarget ? 'Target' : 'Actual';
      dateID = date.dates.dateID;
      pipelineDate = date.dates.date;
    }

    if (dateID) {
      const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
        ConfirmationModalComponent,
        {
          width: '600px',
          height: '380px',
          data: {
            modalTitle: 'Delete Pipeline Milestone Date',
            actionMessage:
              'Are you sure you want to delete the Pipeline Milestone Date?',
            modalInformation: [
              {
                title: 'Pipeline',
                info: this.statusSequenceDates[index].sequence.name,
              },
              {
                title: 'Milestone',
                info: this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].statusName,
              },
              {
                title: 'Date Type',
                info: dateType,
              },
              {
                title: 'Date',
                info: pipelineDate,
              },
            ],
          },
          backdropClass: 'confirmDialogComponent',
          hasBackdrop: true,
        }
      );
      ref.afterClosed().subscribe((data) => {
        if (data.clicked === 'Ok') {
          this.apollo
            .mutate({
              mutation: DELETE_PIPELINE_PROJECT_DATE,
              variables: {
                id: dateID,
              },
            })
            .subscribe(({ data }) => {
              // @ts-ignore
              let results = data.deletePipelineProjectDate;

              if (date.isInitial) {
                this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].dates.initialDateID = '';
                this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].dates.initialDate = '';
                this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].dates.newInitialDate = '';
              } else {
                this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].dates.dateID = '';
                this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].dates.date = '';
                this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].dates.newDate = '';
              }

              this.dateDelete = null;
              this.openSnackBar('Deleted the Pipeline Project Date.');
            });
        }
      });
    } else {
      if (date.isInitial) {
        this.statusSequenceDates[index].statusDates[
          date.index - 1
        ].dates.newInitialDate = '';
      } else {
        this.statusSequenceDates[index].statusDates[
          date.index - 1
        ].dates.newDate = '';
      }
    }
  }

  deleteForecastDateConfirmation(date: any, index: number) {
    let dateType: string = 'Forecast Approval';
    let pipelineDate: string = date.forecastDates.date;

    if (date.forecastDates.id) {
      const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
        ConfirmationModalComponent,
        {
          width: '600px',
          height: '380px',
          data: {
            modalTitle: 'Delete Forecast Approval Date',
            actionMessage:
              'Are you sure you want to delete the Forecast Approval Date?',
            modalInformation: [
              {
                title: 'Pipeline',
                info: this.statusSequenceDates[index].sequence.name,
              },
              {
                title: 'Milestone',
                info: this.statusSequenceDates[index].statusDates[
                  date.index - 1
                ].statusName,
              },
              {
                title: 'Date Type',
                info: dateType,
              },
              {
                title: 'Date',
                info: pipelineDate,
              },
            ],
          },
          backdropClass: 'confirmDialogComponent',
          hasBackdrop: true,
        }
      );
      ref.afterClosed().subscribe((data) => {
        if (data.clicked === 'Ok') {
          this.apollo
            .mutate({
              mutation: delete_forecast_approval_date,
              variables: {
                id: date.forecastDates.id,
                // dateTypeID: date.forecastDates.dateTypeID
              },
            })
            .subscribe(({ data }) => {
              // @ts-ignore
              let results = data.deleteForecastApprovalDate;

              this.statusSequenceDates[index].statusDates[
                date.index - 1
              ].forecastDates.id = '';
              this.statusSequenceDates[index].statusDates[
                date.index - 1
              ].forecastDates.date = '';
              this.statusSequenceDates[index].statusDates[
                date.index - 1
              ].forecastDates.newDate = '';

              this.dateDelete = null;
              this.openSnackBar('Deleted the Forecast Approval Date.');
            });
        }
      });
    } else {
      this.statusSequenceDates[index].statusDates[
        date.index - 1
      ].forecastDates.newDate = '';
    }
  }

  passDate(dates: any) {
    let updateDates = [];
    let updateForecastApproval = [];

    for (let i = 0; i < dates.statusDates.length; i++) {
      if (
        dates.statusDates[i].dates.newDate !== null &&
        dates.statusDates[i].dates.newDate !== ''
      ) {
        let formatDate = moment
          .utc(dates.statusDates[i].dates.newDate)
          .format('MM/DD/YYYY');

        if (
          formatDate !== dates.statusDates[i].dates.date ||
          dates.statusDates[i].dates.isTarget !==
            dates.statusDates[i].dates.newIsTarget
        ) {
          if (
            dates.statusDates[i].dates.dateID !== null &&
            dates.statusDates[i].dates.dateID !== ''
          ) {
            updateDates.push({
              id: dates.statusDates[i].dates.dateID,
              date: moment
                .utc(dates.statusDates[i].dates.newDate)
                .format('YYYY-MM-DD'),
              target: dates.statusDates[i].dates.newIsTarget,
            });
          } else {
            updateDates.push({
              pipeline_milestone_id: dates.statusDates[i].pmid,
              pipeline_project_id: dates.sequence.pipelineProjectID,
              initial_target: false,
              target: dates.statusDates[i].dates.newIsTarget,
              date: moment
                .utc(dates.statusDates[i].dates.newDate)
                .format('YYYY-MM-DD'),
            });
          }
        }
      }

      if (
        dates.statusDates[i].dates.newInitialDate !== null &&
        dates.statusDates[i].dates.newInitialDate !== ''
      ) {
        let formatInitialDate = moment
          .utc(dates.statusDates[i].dates.newInitialDate)
          .format('MM/DD/YYYY');

        if (formatInitialDate !== dates.statusDates[i].dates.initialDate) {
          if (
            dates.statusDates[i].dates.initialDateID !== null &&
            dates.statusDates[i].dates.initialDateID !== ''
          ) {
            updateDates.push({
              id: dates.statusDates[i].dates.initialDateID,
              date: moment
                .utc(dates.statusDates[i].dates.newInitialDate)
                .format('YYYY-MM-DD'),
              target: true,
            });
          } else {
            updateDates.push({
              pipeline_milestone_id: dates.statusDates[i].pmid,
              pipeline_project_id: dates.sequence.pipelineProjectID,
              initial_target: true,
              target: true,
              date: moment
                .utc(dates.statusDates[i].dates.newInitialDate)
                .format('YYYY-MM-DD'),
            });
          }
        }
      }

      if (
        dates.statusDates[i].forecastDates &&
        dates.statusDates[i].forecastDates.newDate !== null &&
        dates.statusDates[i].forecastDates.newDate !== ''
      ) {
        let formatForecastDate = moment
          .utc(dates.statusDates[i].forecastDates.newDate)
          .format('MM/DD/YYYY');
        if (formatForecastDate !== dates.statusDates[i].forecastDates.date) {
          if (
            dates.statusDates[i].forecastDates.id !== null &&
            dates.statusDates[i].forecastDates.id !== ''
          ) {
            updateForecastApproval.push({
              id: dates.statusDates[i].forecastDates.id,
              date: moment
                .utc(dates.statusDates[i].forecastDates.newDate)
                .format('YYYY-MM-DD'),
              project_id: this.projectID,
              date_type_id: this.forecastApprovalID,
              pipeline_milestone_id: dates.statusDates[i].pmid,
            });
          } else {
            updateForecastApproval.push({
              project_id: this.projectID,
              date_type_id: this.forecastApprovalID,
              pipeline_milestone_id: dates.statusDates[i].pmid,
              date: moment
                .utc(dates.statusDates[i].forecastDates.newDate)
                .format('YYYY-MM-DD'),
            });
          }
        }
      }
    }

    if (updateDates.length > 0 && updateForecastApproval.length > 0) {
      this.apollo
        .mutate({
          mutation: update_pipeline_project_and_forecast_approvals,
          variables: {
            input: {
              id: dates.sequence.pipelineProjectid,
              pipelineProjectDates: {
                upsert: updateDates,
              },
            },
            forecast: {
              id: this.forecastApprovalID,
              projectMilestoneDates: {
                upsert: updateForecastApproval,
              },
            },
            projectID: this.projectID,
          },
        })
        .subscribe(
          ({ data }) => {
            // @ts-ignore
            let results = data.updatePipelineProject;
            // @ts-ignore
            let forecastResults = data.updateProjectMilestoneDates;

            this.dates = results.project.pipelineProjects;
            this.forecastDates = forecastResults.projectMilestoneDates;
            this.statusSequenceDates = this.formatDates();
            this.openSnackBar('Successfully Updated Deal Dates on Pipeline');
            this.dealDatesService.refreshDates(this.projectID);
          },
          (error) => {
            this.statusSequenceDates = this.formatDates();
          }
        );
    } else if (updateForecastApproval.length > 0) {
      this.apollo
        .mutate({
          mutation: update_forecast_approvals,
          variables: {
            input: {
              id: this.forecastApprovalID,
              projectMilestoneDates: {
                upsert: updateForecastApproval,
              },
            },
            projectID: this.projectID,
          },
        })
        .subscribe(
          ({ data }) => {
            // @ts-ignore
            let forecastResults = data.updateProjectMilestoneDates;
            this.forecastDates = forecastResults.projectMilestoneDates;
            this.statusSequenceDates = this.formatDates();
            this.openSnackBar('Successfully Updated Deal Dates on Pipeline');
            this.dealDatesService.refreshDates(this.projectID);
          },
          (error) => {
            this.statusSequenceDates = this.formatDates();
          }
        );
    } else if (updateDates.length > 0) {
      this.apollo
        .mutate({
          mutation: UPDATE_PIPELINE_PROJECT,
          variables: {
            input: {
              id: dates.sequence.pipelineProjectid,
              pipelineProjectDates: {
                upsert: updateDates,
              },
            },
          },
        })
        .subscribe(
          ({ data }) => {
            // @ts-ignore
            let results = data.updatePipelineProject;

            this.dates = results.project.pipelineProjects;
            this.statusSequenceDates = this.formatDates();
            this.openSnackBar('Successfully Updated Deal Dates on Pipeline');
            this.dealDatesService.refreshDates(this.projectID);
          },
          (error) => {
            this.statusSequenceDates = this.formatDates();
          }
        );
    }
  }

  handleMissingData(data: any) {
    this.requirementsChange.emit(data);
  }

  passStatuses(data: any) {
    this.statuses = data.projectStatuses;
    this.statusesChange.emit(this.statuses);
    this.dates = data.pipelineProjects;
    this.datesChange.emit(this.dates);
    this.dealDatesService.refreshDates(this.projectID);
    this.statusSequenceDates = this.formatDates();
    this.applicationsChange.emit(data.applications);
  }
}
